import React from 'react';
import { Box, Typography, Skeleton, CircularProgress } from '@mui/material';
import { EmployeeAvatar } from '../../../../../../common/EmployeeAvatar';
import { 
  getColor, 
  getDisplayName, 
  getDescription,
  getDefaultLevelId
} from '../../../../../../../constants/skillLevels';

const ReviewerCompetencyRadio = ({ 
  reviewer, 
  competencyLevelId,
  onLevelSelect,
  isInteractive = false,
  opacity = 1,
  additionalInfo = null,
  skillName = '',
  customDescriptions = null,
  isLoadingDescriptions = false,
  skillLevels,
  limitedView = false,
  loading = false,
  showSkillLevels = true
}) => {
  const defaultLevelId = getDefaultLevelId(skillLevels);
  const levelId = competencyLevelId || defaultLevelId;
  
  const handleSelect = (levelId) => {
    if (isInteractive && onLevelSelect && !loading) {
      onLevelSelect(levelId);
    }
  };

  // Get the appropriate description for a competency level
  const getCompetencyDescription = (level) => {
    // If we have custom descriptions and this level has one, use it
    if (customDescriptions && customDescriptions[level]) {
      return customDescriptions[level];
    }
    // Otherwise fall back to the default description
    return getDescription(level, skillLevels);
  };

  // Determine if description should be shown for this level
  const shouldShowDescription = (level) => {
    if (!limitedView) return true; // Always show if not in limited view
    return level.levelId === levelId; // In limited view, only show for selected level
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Reviewer Information */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, opacity: opacity }}>
        <EmployeeAvatar employee={reviewer.employee} size={32} />
        <Box>
          <Typography sx={{ fontWeight: 500 }}>
            {reviewer.employee.fullName}
          </Typography>
          {additionalInfo && (
            <Typography variant="caption" color="text.secondary">
              {additionalInfo}
            </Typography>
          )}
        </Box>
      </Box>


      {/* Competency Cards */}
      {showSkillLevels && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: 1.5,
            mt: 1
          }}
        >
          {skillLevels?.map((level) => {
            const isSelected = level.levelId === levelId;
            const showDescription = shouldShowDescription(level);

            return (
              <Box
                key={level.levelId}
                onClick={() => isInteractive && !loading && handleSelect(level.levelId)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '12px 16px',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isSelected ? getColor(level.levelId, skillLevels) : 'divider',
                  backgroundColor: isSelected ? `${getColor(level.levelId, skillLevels)}15` : 'transparent',
                  opacity: isInteractive ? 1 : opacity,
                  transition: 'all 0.2s ease',
                  cursor: isInteractive && !loading ? 'pointer' : 'default',
                  position: 'relative',
                  minHeight: limitedView && !isSelected ? '40px' : 'auto',
                  '&:hover': {
                    opacity: isInteractive && !loading ? 0.9 : opacity,
                    backgroundColor: isInteractive && !loading ? `${getColor(level.levelId, skillLevels)}10` : isSelected ? `${getColor(level.levelId, skillLevels)}15` : 'transparent',
                    borderColor: isInteractive && !loading ? getColor(level.levelId, skillLevels) : isSelected ? getColor(level.levelId, skillLevels) : 'divider',
                    transform: isInteractive && !loading ? 'translateY(-2px)' : 'none',
                    boxShadow: isInteractive && !loading ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                  }
                }}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: getColor(level.levelId, skillLevels),
                      borderTopLeftRadius: '4px',
                      borderTopRightRadius: '4px'
                    }}
                  />
                )}
                
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getColor(level.levelId, skillLevels),
                          border: '1px solid #333'
                        }}
                      />
                      <Typography sx={{ 
                        fontSize: '14px',
                        fontWeight: isSelected ? 600 : 500,
                      }}>
                        {getDisplayName(level.levelId, skillLevels)}
                      </Typography>
                    </Box>
                    
                    {isSelected && loading && (
                      <CircularProgress size={14} thickness={4} color="inherit" />
                    )}
                  </Box>
                  
                  {showDescription && (
                    isLoadingDescriptions ? (
                      <Skeleton 
                        variant="text" 
                        width="100%" 
                        height={40} 
                        sx={{ ml: 3, mt: 0.5 }} 
                      />
                    ) : (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: '11px',
                          lineHeight: 1.4,
                          whiteSpace: 'normal',
                          wordBreak: 'normal'
                        }}
                      >
                        {getCompetencyDescription(level.levelId)}
                      </Typography>
                    )
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ReviewerCompetencyRadio; 